import {useCallback} from 'react';
import type {
  ChatAttachment,
  ChatMessage,
  ChatRepository,
  ChatUserInput,
} from './types';

export interface UseChatResult {
  messages: ChatMessage[];
  isSubmitting: boolean;
  isAwaitingResponse: boolean;
  error: string | null;
  /**
   * Returns whether the message was accepted. A blank message or a turn
   * already in flight turns it away; callers keep the draft in that case.
   */
  submitMessage: (message: string, attachments?: ChatAttachment[]) => boolean;
  reset: () => void;
  /** Send the last message again after a failure. */
  retry?: () => boolean;
  /** Present only when the repository can stop a turn mid-flight. */
  cancel?: () => Promise<void>;
  isCancelling: boolean;
  userInput?: ChatUserInput;
}

/**
 * Thin orchestration wrapper over ChatRepository.
 * Guards submission and provides a clean consumer API.
 */
export function useChat(repo: ChatRepository): UseChatResult {
  const {sendMessage, resetChat, cancelStream} = repo;

  const submitMessage = useCallback(
    (text: string, attachments?: ChatAttachment[]) => {
      const trimmed = text.trim();
      if (!trimmed) {
        return false;
      }
      return sendMessage(trimmed, attachments);
    },
    [sendMessage],
  );

  const reset = useCallback(() => {
    resetChat();
  }, [resetChat]);

  return {
    messages: repo.messages,
    isSubmitting: repo.isStreaming,
    isAwaitingResponse: repo.isAwaitingResponse,
    error: repo.error,
    submitMessage,
    reset,
    retry: repo.retry,
    cancel: cancelStream,
    isCancelling: repo.isCancelling ?? false,
    userInput: repo.userInput,
  };
}
